
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { fetchGalleryByEvents } from '@/services/api';
import ImageModal from '@/components/ImageModal';
import { Helmet } from 'react-helmet-async';
import SeoHead from '@/components/Seo';
import { constants } from 'fs/promises';

interface ImageItem {
    id: number;
    source: string;
    caption: string | null;
    title: string;
    created_at: string;
}

const AlbumPage = () => {
    const { id } = useParams<{ id: string }>();

    const [images, setImages] = useState<ImageItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState('');
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [ogImage, setOgImage] = useState(null)

    const handleImageClick = (url: string) => {
        setSelectedImage(url);
    };

    const closeModal = () => {
        setSelectedImage(null);
    };

    useEffect(() => {
        if (!id) return;

        const fetchAlbum = async () => {
            try {
                const response = await fetchGalleryByEvents(id!)
                const data = response.data;
                setImages(data.images || []);
                setTitle(data.title || 'Gallery');
                const ogImageI = data.images.length > 0
                    ? data.images[0].url // or images[0].path, depending on your data
                    : null;
                setOgImage(ogImageI);

            } catch (err) {
                console.error('Failed to fetch album', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAlbum();
    }, [id]);

    if (loading) return <div className="p-10 text-center text-gray-500">Loading album...</div>;

    return (
        <>
            <SeoHead
                title={title}
                description={`${title} — Photo highlights from the Diocese of Ibadan South, capturing moments of worship, fellowship, and faith.`}
                ogImage={ogImage}
            />
            <div className="min-h-screen py-16 px-4 bg-white">
                <div className="max-w-6xl mx-auto">
                    <h1 className="text-3xl font-bold mb-8">{title}</h1>
                    {images.length === 0 ? (
                        <p className="text-gray-500">No photos available for this album.</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                            {images.map((img) => (
                                <motion.div
                                    key={img.id}
                                    whileHover={{ scale: 1.02 }}
                                    className="overflow-hidden rounded-xl shadow-md cursor-pointer group"
                                    onClick={() => handleImageClick(img.source)}
                                >
                                    <img
                                        src={img.source}
                                        alt={img.caption || img.title}
                                        className="object-cover w-full h-48 group-hover:brightness-90 transition-all"
                                    />
                                    {img.caption && (
                                        <div className="p-2 text-sm text-muted-foreground bg-muted">
                                            {img.caption}
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    )}
                    {selectedImage && <ImageModal imageUrl={selectedImage} onClose={closeModal} />}
                </div>
            </div>
        </>
    );
};

export default AlbumPage;
